import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, RefreshCw } from 'lucide-react';

interface ErrorAppDetailProps {
  error?: Error | null;
  onRetry?: () => void;
  retryCount?: number;
}

const ErrorAppDetail: React.FC<ErrorAppDetailProps> = ({ error, onRetry, retryCount = 0 }) => {
  const navigate = useNavigate();

  const getErrorMessage = () => {
    if (!error) return 'App not found';
    
    const message = error.message;
    if (message.includes('timeout') || message.includes('timed out')) {
      return 'The request took too long to complete. Please check your internet connection and try again.';
    }
    if (message.includes('connection') || message.includes('network')) {
      return 'Unable to connect to the app store. Please check your internet connection.';
    }
    if (message.includes('not found')) {
      return 'The app you are looking for could not be found. It may have been removed or is not available for your device.';
    }
    
    return message;
  };

  const shouldShowRetry = () => {
    return onRetry && error && !error.message.includes('not found');
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="text-center max-w-md" role="alert">
        <h1 className="text-2xl font-bold text-foreground mb-2">
          {error ? 'Error Loading App' : 'App Not Found'}
        </h1>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          {getErrorMessage()}
        </p>
        
        {retryCount > 0 && (
          <p className="text-sm text-muted-foreground mb-4">
            Retry attempts: {retryCount}
          </p>
        )}
        
        <div className="flex flex-col space-y-3">
          {shouldShowRetry() && (
            <Button 
              onClick={onRetry} 
              variant="default"
              className="w-full active:scale-[0.97] transition-transform"
            >
              <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
              Try Again
            </Button>
          )}
          
          <Button 
            onClick={handleBack} 
            variant="outline"
            className="w-full active:scale-[0.97] transition-transform"
          >
            <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
            Back to Apps
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ErrorAppDetail;
